import { useEffect, useState } from 'react';
import { useCampaignStore } from '@/stores';

/**
 * PPR-012: shared copy fragment for both the onboarding tooltip and the
 * inbox banner. Exported so the onboarding page can reuse the exact
 * wording without literal duplication (the ticket says "same banner
 * re-appears" — copy parity matters).
 */
export const ANEDOT_GROSS_DEPOSITS_INSTRUCTION =
  'The default mode hides the per-payout fee breakdown we need for compliant filing. '
  + "Request 'Gross Deposits' mode from Anedot support if you haven't already.";

/**
 * PPR-012: educational toast surfaced the first time a treasurer uploads
 * an Anedot file. Anedot's default export mode hides the per-payout fee
 * breakdown, which makes a compliant AL FCPA filing impossible. The
 * banner tells the user to request "Gross Deposits" mode from Anedot
 * support; once dismissed it stays dismissed per-campaign via
 * localStorage so it doesn't nag on every subsequent Anedot upload.
 *
 * Per-campaign scoping is intentional: a treasurer running two
 * campaigns can see the banner once per campaign rather than seeing it
 * once globally (it&apos;s easy to forget the message when switching
 * campaigns, especially if the second campaign is a different staffer&rsquo;s
 * responsibility).
 */
export function AnedotGrossDepositsBanner() {
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id ?? '');
  const storageKey = `ppr012:anedot-gross-deposits-dismissed:${campaignId}`;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!campaignId) return;
    try {
      setDismissed(window.localStorage.getItem(storageKey) === '1');
    } catch {
      // localStorage unavailable (private browsing, etc.) — banner stays
      // shown and the "Got it" click below silently no-ops. The banner is
      // educational, not functional, so a failed dismiss is acceptable.
    }
  }, [campaignId, storageKey]);

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(storageKey, '1');
    } catch {
      // see comment in useEffect — silent failure is fine.
    }
  };

  return (
    <div
      role="status"
      data-testid="anedot-gross-deposits-banner"
      className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start justify-between gap-3"
    >
      <div>
        <strong className="block font-medium">
          Anedot file detected
        </strong>
        <p className="mt-1">
          Make sure your Anedot account is in <em>Gross Deposits</em> mode.
          {' '}{ANEDOT_GROSS_DEPOSITS_INSTRUCTION}
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="shrink-0 rounded-md border border-amber-400 bg-white px-2 py-1 text-xs font-medium hover:bg-amber-100"
      >
        Got it
      </button>
    </div>
  );
}
